import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { resolveImageSrc } from "@/lib/api";
import {
  MapPin, Globe, ArrowLeft, Share2, MessageSquare, Star, Calendar, Sparkles, Heart,
} from "lucide-react";
import RatingsPanel, { RatingSummaryInline } from "@/components/RatingsPanel";
import SelfRatingsTrend from "@/components/SelfRatingsTrend";
import SocialLinkPill from "@/components/SocialLinkPill";
import SharePreviewDialog from "@/components/SharePreviewDialog";
import VendorTrustBadge from "@/components/VendorTrustBadge";
import BookNowDialog from "@/components/BookNowDialog";
import DonateDialog from "@/components/DonateDialog";
import NonprofitDonationCard from "@/components/NonprofitDonationCard";
import PrivateBookingDialog from "@/components/PrivateBookingDialog";
import PublicCalendarSection from "@/components/PublicCalendarSection";
import FollowButton from "@/components/FollowButton";
import { Button } from "@/components/ui/button";

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const DEFAULT_OG_IMAGE = "https://images.unsplash.com/photo-1533900298318-6b8da08a523e?w=1200&h=630&fit=crop";

/**
 * Public vendor profile — the page a customer lands on when they scan the
 * booth QR. Mobile-first stacked cards, ~1 tap per action.
 *
 * Section order (top to bottom):
 *   1. Hero      — logo, name, verified badge, categories, inline rating
 *   2. CTA row   — Book now + Share + Leave a review
 *   3. About     — bio
 *   4. Photos    — portfolio_images gallery
 *   5. Upcoming  — approved future events
 *   6. Reviews   — full ratings panel + link to customer rating page
 *   7. Contact   — location, website, social — no email exposed
 */
export default function PublicVendorProfile({ slugOverride } = {}) {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const [vendor, setVendor] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError("");
    api.get(`/vendors/by-slug/${slug}`)
      .then(({ data }) => setVendor(data))
      .catch(() => setError("This maker doesn't exist yet."))
      .finally(() => setLoading(false));
  }, [slug]);

  // Load upcoming approved events in parallel — don't block the main render.
  useEffect(() => {
    if (!slug) return;
    api.get(`/vendors/by-slug/${slug}/upcoming-events?limit=6`)
      .then(({ data }) => setEvents(data?.items || data || []))
      .catch(() => setEvents([]));
  }, [slug]);

  useEffect(() => {
    if (!vendor) return;
    const name = vendor.business_name || vendor.name;
    const desc = (vendor.bio || `Discover ${name} on AddVendTour — a local maker building beautiful work for community events.`).replace(/\s+/g, " ").trim().slice(0, 200);
    const image = vendor.portfolio_images?.[0] || vendor.logo_url || DEFAULT_OG_IMAGE;
    const url = `${window.location.origin}/@${vendor.slug}`;
    const title = `${name} · AddVendTour`;
    document.title = title;
    setMeta("name", "description", desc);
    setCanonical(url);
    setMeta("property", "og:site_name", "AddVendTour");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:image", image);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:image:alt", `${name} on AddVendTour`);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", "profile");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", image);
    setMeta("name", "twitter:image:alt", `${name} on AddVendTour`);
    return () => {
      const siteTitle = "AddVendTour — Local makers meet community events";
      const siteDesc = "Discover, apply, and get seen at pop-ups and markets in your city. Built for makers and organizers.";
      const siteImg = DEFAULT_OG_IMAGE;
      const siteUrl = window.location.origin + "/";
      document.title = siteTitle;
      setMeta("name", "description", siteDesc);
      setCanonical(siteUrl);
      setMeta("property", "og:site_name", "AddVendTour");
      setMeta("property", "og:title", siteTitle);
      setMeta("property", "og:description", siteDesc);
      setMeta("property", "og:image", siteImg);
      setMeta("property", "og:url", siteUrl);
      setMeta("property", "og:type", "website");
      setMeta("name", "twitter:title", siteTitle);
      setMeta("name", "twitter:description", siteDesc);
      setMeta("name", "twitter:image", siteImg);
    };
  }, [vendor]);

  if (loading) return <div className="p-20 text-center font-serif text-2xl">Loading…</div>;
  if (error || !vendor) return (
    <div className="p-20 text-center">
      <div className="font-serif text-4xl">{error || "Not found."}</div>
      <Link to="/events" className="mt-6 inline-block btn-primary px-6 py-3 rounded-full">Browse events</Link>
    </div>
  );

  const displayName = vendor.business_name || vendor.name;

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-8 sm:py-12">
      <Link
        to="/events"
        className="text-sm inline-flex items-center gap-1 mb-6"
        style={{ color: "var(--ink-soft)" }}
        data-testid="public-back"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to events
      </Link>

      {/* --- Section 1: Hero card ------------------------------------- */}
      <section
        className="card-warm rounded-sm p-5 sm:p-6"
        data-testid="public-vendor-hero"
      >
        <div className="flex items-start gap-4">
          {vendor.logo_url ? (
            <img
              src={resolveImageSrc(vendor.logo_url)}
              alt={`${displayName} logo`}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-sm object-cover shrink-0"
              style={{ border: "1px solid var(--sand)" }}
              data-testid="public-vendor-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-sm flex items-center justify-center font-serif text-4xl shrink-0"
              style={{ backgroundColor: "var(--sand)", color: "var(--terracotta)" }}
              data-testid="public-vendor-logo-fallback"
            >
              {(displayName || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="overline" style={{ color: "var(--terracotta)" }}>
              @{vendor.slug}
            </div>
            <h1
              className="font-serif text-3xl sm:text-4xl mt-1 leading-tight break-words"
              data-testid="public-vendor-name"
            >
              {displayName}
            </h1>
            {vendor.categories?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {vendor.categories.slice(0, 4).map((c) => (
                  <span
                    key={c}
                    className="px-2 py-0.5 rounded-full text-[11px]"
                    style={{ backgroundColor: "var(--sand)", color: "var(--ink-soft)" }}
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4">
          <RatingSummaryInline
            summary={vendor.ratings_summary}
            selfHidden={vendor.ratings_self_hidden}
          />
        </div>
      </section>

      {/* --- Section 2: CTA row (mobile-first primary actions) -------- */}
      <div className="mt-3 grid grid-cols-2 gap-2" data-testid="public-vendor-cta-row">
        <Button
          type="button"
          data-testid="public-vendor-book-btn"
          onClick={() => setBookOpen(true)}
          className="btn-primary rounded-full py-5 col-span-2 sm:col-span-1"
        >
          <MessageSquare className="h-4 w-4 mr-1.5" /> Book now
        </Button>
        <Button
          type="button"
          data-testid="public-vendor-share-btn"
          onClick={() => setShareOpen(true)}
          className="btn-outline rounded-full py-5"
        >
          <Share2 className="h-4 w-4 mr-1.5" /> Share
        </Button>
        <div className="flex items-center justify-center">
          <FollowButton
            vendorId={vendor.id}
            vendorName={vendor.business_name || vendor.name}
            size="lg"
            className="py-5 w-full"
          />
        </div>
        <Link
          to={`/rate/${vendor.slug}`}
          data-testid="public-vendor-review-link"
          className="btn-outline rounded-full py-3 text-sm inline-flex items-center justify-center gap-1.5"
        >
          <Star className="h-4 w-4" /> Leave a review
        </Link>
      </div>

      {/* --- Section 3: About ---------------------------------------- */}
      <NonprofitDonationCard vendor={vendor} onDonateClick={() => setDonateOpen(true)} />

      {/* Private booking CTA — the "book me for a private event" flow that
          fires off the vendor's QR code. Only visible when the vendor has
          Stripe Connect ready; otherwise we render a soft explanation. */}
      <section
        className="card-warm rounded-sm p-5 sm:p-6 mt-4"
        data-testid="public-vendor-private-booking-cta"
      >
        <div className="overline mb-2" style={{ color: "var(--avt-purple)" }}>
          Book for a private event
        </div>
        <p className="text-sm mb-3 flex flex-wrap items-center gap-2" style={{ color: "var(--ink-soft)" }}>
          <span>
            Birthday, wedding, corporate — share your date + budget and{" "}
            <strong>{vendor.business_name || vendor.name}</strong> will reply
            with a firm quote. Pay only if you accept, protected by Stripe.
          </span>
          <VendorTrustBadge verified={!!vendor.trust_verified} />
        </p>
        <PrivateBookingDialog
          vendorSlug={vendor.slug}
          vendorName={vendor.business_name || vendor.name}
          canBook={!!vendor.private_bookings_enabled}
          disabledReason={
            !vendor.private_bookings_enabled
              ? "This vendor hasn't finished payment setup yet. Come back soon."
              : null
          }
        />
      </section>

      {/* Public calendar — only renders if the owner has flipped at least
          one event public. See MyCalendarPage for the owner's controls. */}
      <PublicCalendarSection
        slug={vendor.slug}
        displayName={vendor.business_name || vendor.name}
      />

      {vendor.bio && (
        <section
          className="card-warm rounded-sm p-5 sm:p-6 mt-4"
          data-testid="public-vendor-about"
        >
          <div className="overline mb-2" style={{ color: "var(--moss)" }}>About</div>
          <p
            className="text-[15px] leading-relaxed"
            data-testid="public-vendor-bio"
            style={{ color: "var(--ink)" }}
          >
            {vendor.bio}
          </p>
        </section>
      )}

      {/* --- Section 4: Photos --------------------------------------- */}
      {vendor.portfolio_images?.length > 0 && (
        <section
          className="card-warm rounded-sm p-5 sm:p-6 mt-4"
          data-testid="public-portfolio"
        >
          <div className="overline mb-3" style={{ color: "var(--moss)" }}>
            Photos · {vendor.portfolio_images.length}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {vendor.portfolio_images.slice(0, 6).map((img, i) => (
              <PublicPhotoTile
                key={i}
                src={resolveImageSrc(img)}
                alt={`${displayName} portfolio ${i + 1}`}
                testId={`public-portfolio-img-${i}`}
              />
            ))}
          </div>
        </section>
      )}

      {/* --- Section 5: Upcoming events ------------------------------ */}
      {events.length > 0 && (
        <section
          className="card-warm rounded-sm p-5 sm:p-6 mt-4"
          data-testid="public-vendor-upcoming"
        >
          <div className="overline mb-3" style={{ color: "var(--moss)" }}>
            <Calendar className="h-3.5 w-3.5 inline mr-1" /> Catch them at
          </div>
          <div className="space-y-2">
            {events.slice(0, 6).map((ev) => (
              <Link
                key={ev.id}
                to={`/events/${ev.id}`}
                data-testid={`public-upcoming-${ev.id}`}
                className="block rounded-sm p-3 -mx-1 hover:bg-[color:var(--sand)] transition-colors"
              >
                <div className="text-[11px] uppercase tracking-widest"
                  style={{ color: "var(--ink-soft)" }}>
                  {formatShortDate(ev.date)} · {ev.city}
                </div>
                <div className="font-serif text-lg mt-0.5">{ev.title}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* --- Section 6: Reviews -------------------------------------- */}
      <section
        className="card-warm rounded-sm p-5 sm:p-6 mt-4"
        data-testid="public-vendor-reviews"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="overline" style={{ color: "var(--moss)" }}>Ratings & reviews</div>
          <Link
            to={`/rate/${vendor.slug}`}
            data-testid="public-vendor-write-review"
            className="text-xs underline whitespace-nowrap"
            style={{ color: "var(--moss)" }}
          >
            <Sparkles className="h-3 w-3 inline mr-0.5" /> Add yours
          </Link>
        </div>
        {vendor.ratings_self_hidden ? (
          <SelfRatingsTrend role="vendor" />
        ) : (
          <RatingsPanel
            userId={vendor.id}
            role="vendor"
            initialSummary={vendor.ratings_summary}
          />
        )}
      </section>

      {/* --- Section 7: Contact card --------------------------------- */}
      <section
        className="card-warm rounded-sm p-5 sm:p-6 mt-4"
        data-testid="public-vendor-contact"
      >
        <div className="overline mb-3" style={{ color: "var(--moss)" }}>Get in touch</div>
        <div className="space-y-3 text-sm" style={{ color: "var(--ink)" }}>
          {vendor.location && (
            <div className="flex items-center gap-2" data-testid="public-vendor-location">
              <MapPin className="h-4 w-4 shrink-0" style={{ color: "var(--ink-soft)" }} />
              <span>{vendor.location}</span>
            </div>
          )}
          {vendor.website && (
            <div className="flex items-center gap-2" data-testid="public-vendor-website">
              <Globe className="h-4 w-4 shrink-0" style={{ color: "var(--ink-soft)" }} />
              <a
                href={vendor.website}
                target="_blank"
                rel="noreferrer"
                className="underline break-all"
              >
                {vendor.website.replace(/^https?:\/\//, "")}
              </a>
            </div>
          )}
          {vendor.social_url && (
            <div data-testid="public-vendor-social-row">
              <SocialLinkPill url={vendor.social_url} testId="public-vendor-social" />
            </div>
          )}
        </div>
        <Button
          type="button"
          data-testid="public-vendor-book-btn-bottom"
          onClick={() => setBookOpen(true)}
          className="btn-primary rounded-full mt-4 w-full py-5"
        >
          <MessageSquare className="h-4 w-4 mr-1.5" /> Send a message
        </Button>
        <p
          className="text-[11px] mt-2 text-center"
          style={{ color: "var(--ink-soft)" }}
        >
          Your message goes straight to {displayName}. Their email stays private.
        </p>
      </section>

      <BookNowDialog
        open={bookOpen}
        onOpenChange={setBookOpen}
        slug={vendor.slug}
        vendorName={displayName}
      />
      <DonateDialog
        open={donateOpen}
        onOpenChange={setDonateOpen}
        nonprofitUserId={vendor.id}
        orgName={vendor.nonprofit_org_name || vendor.business_name || displayName}
      />
      <SharePreviewDialog
        vendor={vendor}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
    </div>
  );
}

function formatShortDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * PublicPhotoTile — single portfolio photo with graceful fallback.
 * Same shape as VendorProfile's PortfolioTile but read-only (no remove
 * button) since this renders on the public-facing profile.
 */
function PublicPhotoTile({ src, alt, testId }) {
  const [broken, setBroken] = useState(false);
  if (broken || !src) {
    return (
      <div className="aspect-square rounded-sm overflow-hidden flex items-center justify-center"
           style={{ backgroundColor: "var(--sand)", color: "var(--ink-soft)" }}
           data-testid={testId}>
        <span className="text-[10px] uppercase tracking-widest">Photo unavailable</span>
      </div>
    );
  }
  return (
    <div className="aspect-square rounded-sm overflow-hidden">
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
        data-testid={testId}
        onError={() => setBroken(true)}
      />
    </div>
  );
}

